"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { ArrowRight, Eye, EyeOff, Mail, Lock } from "lucide-react";
import { useTranslations } from "next-intl";

export function LoginForm({ error, returnTo }: { error?: string; returnTo?: string }) {
  const t = useTranslations("Auth");
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);

  const ERRORS: Record<string, string> = {
    invalid_state: t("errorInvalidState"),
    exchange_failed: t("errorExchangeFailed"),
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setLoading(true);
    const form = new FormData(e.currentTarget);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: form.get("email"), password: form.get("password") }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setFormError(data?.error ?? t("errorGeneric"));
        setLoading(false);
        return;
      }
      router.push(returnTo ?? "/beranda");
    } catch {
      setFormError(t("errorNetwork"));
      setLoading(false);
    }
  }

  const queryError = error ? (ERRORS[error] ?? t("errorGeneric")) : null;
  const displayError = formError ?? queryError;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {displayError && (
        <div role="alert" className="flex items-start gap-2.5 rounded-xl bg-red-500/15 px-4 py-3 text-sm text-red-300">
          <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red-400" />
          {displayError}
        </div>
      )}

      {/* Email */}
      <div
        className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all ${
          emailFocused ? "border-primary/60 bg-white/5 shadow-[0_0_0_3px_rgba(241,102,6,0.15)]" : "border-white/10 bg-white/[0.04]"
        }`}
      >
        <Mail className="size-4 shrink-0 text-white/40" aria-hidden />
        <input
          type="email"
          name="email"
          placeholder={t("email")}
          autoComplete="email"
          required
          onFocus={() => setEmailFocused(true)}
          onBlur={() => setEmailFocused(false)}
          className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
        />
      </div>

      {/* Password */}
      <div
        className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all ${
          passFocused ? "border-primary/60 bg-white/5 shadow-[0_0_0_3px_rgba(241,102,6,0.15)]" : "border-white/10 bg-white/[0.04]"
        }`}
      >
        <Lock className="size-4 shrink-0 text-white/40" aria-hidden />
        <input
          type={showPass ? "text" : "password"}
          name="password"
          placeholder={t("password")}
          autoComplete="current-password"
          required
          onFocus={() => setPassFocused(true)}
          onBlur={() => setPassFocused(false)}
          className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30"
        />
        <button
          type="button"
          onClick={() => setShowPass((v) => !v)}
          className="shrink-0 text-white/30 transition-colors hover:text-white/60"
          aria-label={showPass ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
        >
          {showPass ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
        </button>
      </div>

      {/* Forgot password */}
      <div className="-mt-1 text-right">
        <a href="#" className="text-xs font-semibold text-primary/80 transition-colors hover:text-primary">
          {t("forgotPassword")}
        </a>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-60"
        style={{ background: "linear-gradient(to right, #f16606, #c0392b)", boxShadow: "0 8px 24px rgba(241,102,6,0.35)" }}
      >
        {loading ? t("processing") : t("login")}
        {!loading && <ArrowRight className="size-4" aria-hidden />}
      </button>
    </form>
  );
}
